import TextField from '@components/v2/form/TextField/TextField';
import ListSubheader from '@components/v2/ui/List/ListSubheader';
import Typography from '@components/v2/ui/Typography/Typography';
import { Fragment, KeyboardEvent } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { useSearchableMenuList } from './hooks/useSearchableMenuList';
import { SearchableMenuListProps } from './searchableMenuList.types';

const KEYS_HANDLED_BY_THE_MENU = ['Escape', 'Tab', 'ArrowDown', 'ArrowUp'];

const SearchableMenuList = <T,>({ items, item, testId }: SearchableMenuListProps<T>) => {
  const { translate } = useTranslation();
  const { query, setQuery, visibleItems } = useSearchableMenuList({
    items,
    getSearchValue: item.getSearchValue,
  });

  const renderedItems = visibleItems.map((current) => (
    <Fragment key={item.getKey(current)}>{item.render(current)}</Fragment>
  ));

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (KEYS_HANDLED_BY_THE_MENU.includes(event.key)) {
      return;
    }

    event.stopPropagation();

    const [onlyVisibleItem] = visibleItems;

    if (event.key === 'Enter' && visibleItems.length === 1) {
      item.onSelect?.(onlyVisibleItem);
    }
  };

  return (
    <>
      <ListSubheader
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          flexShrink: 0,
          paddingInline: 0,
          paddingBottom: 0.5,
          marginBottom: -0.5,
          lineHeight: 'unset',
          backgroundColor: 'background.paper',
        }}
      >
        <TextField
          autoFocus
          size="small"
          testId={testId}
          value={query}
          placeholder={translate(LangEnum.GLOBAL_SEARCH)}
          slotProps={{ htmlInput: { 'aria-label': translate(LangEnum.GLOBAL_SEARCH) } }}
          onKeyDown={handleKeyDown}
          onChange={(event) => setQuery(event.target.value)}
        />
      </ListSubheader>

      {renderedItems}

      {visibleItems.length === 0 && (
        <Typography component="li" variant="body2" color="text.secondary" sx={{ padding: 1 }}>
          {translate(LangEnum.GLOBAL_SEARCH_EMPTY_RESULTS)}
        </Typography>
      )}
    </>
  );
};

export default SearchableMenuList;
